import { useState, useEffect } from 'react';
import { SET_REGISTRY } from './setRegistry';
import { supabase } from './supabaseClient';
import { getParallelsForType, parallelKey } from './parallelData';
import { CardThumbnail } from './CardImage';

export default function PublicProfile({ username }) {
  const [profile, setProfile] = useState(null);
  const [SETS, setSETS] = useState([]);
  const [cardStates, setCardStates] = useState({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Parse share parameters from URL
  const urlParams = new URLSearchParams(window.location.search);
  const setIdParam = urlParams.get('setId') || '2024-chrome-galaxy';
  const setMeta = SET_REGISTRY.find(s => s.id === setIdParam) || SET_REGISTRY.find(s => s.id === '2024-chrome-galaxy');
  const sharedLists = urlParams.get('lists') ? urlParams.get('lists').split(',') : null;
  const sharedSets = urlParams.get('sets') ? urlParams.get('sets').split(',') : null;
  const sharedParallelsParam = urlParams.get('parallels');
  // parallels param is either a comma-separated list of parallel IDs, '1' (all), or '0' (none)
  const sharedParallelIds = sharedParallelsParam === '0' ? []
    : sharedParallelsParam === '1' || !sharedParallelsParam ? null
    : sharedParallelsParam.split(',');
  const sharedBaseCards = urlParams.get('basecards') !== '0';
  const isSharedView = !!sharedLists;

  useEffect(() => {
    const loadSetData = async () => {
      try {
        const module = await import(`./sets/${setMeta.dataFile}.js`);
        setSETS(module.SETS || []);
      } catch(e) { console.error('Failed to load set data:', e); }
    };
    loadSetData();
  }, [setMeta.dataFile]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      // Load profile (public_profiles view: safe columns of is_public rows only)
      const { data: profileData, error: profileError } = await supabase
        .from('public_profiles')
        .select('*')
        .eq('username', username.toLowerCase())
        .single();

      if (profileError || !profileData || !profileData.is_public) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setProfile(profileData);

      // Load their collection
      const { data: collectionData } = await supabase
        .from('collections')
        .select('card_key, status, sale_price, grading_company, grading_grade, photos_updated_at')
        .eq('user_id', profileData.id);

      if (collectionData) {
        const map = {};
        collectionData.forEach(row => {
          map[row.card_key] = { status: row.status, sale_price: row.sale_price, grading_company: row.grading_company, grading_grade: row.grading_grade, photos_updated_at: row.photos_updated_at };
        });
        setCardStates(map);
      }

      setLoading(false);
    };
    load();
  }, [username]);

  if (loading) return (
    <div style={styles.loadScreen}>
      <div style={styles.loadText}>LOADING PROFILE...</div>
    </div>
  );

  if (notFound) return (
    <div style={styles.loadScreen}>
      <div style={styles.notFound}>
        <div style={styles.loadText}>PROFILE NOT FOUND</div>
        <div style={styles.notFoundSub}>This profile is private or doesn't exist.</div>
        <a href="/" style={styles.homeLink}>← BACK TO CHECKLIST</a>
      </div>
    </div>
  );

  // Calculate stats
  const totalCards = SETS.reduce((sum, s) => sum + s.cards.length, 0);
  const collectedCount = Object.values(cardStates).filter(s => s.status === 'collected').length;
  const pct = totalCards ? Math.round(collectedCount / totalCards * 100) : 0;

  // Build wishlist, trade, for sale, and graded lists
  const wishlistCards = [];
  const tradeCards = [];
  const forSaleCards = [];
  const gradedCards = [];

  const setsToShow = sharedSets
    ? SETS.filter(s => sharedSets.includes(s.type))
    : SETS;

  setsToShow.forEach(set => {
    set.cards.forEach(card => {
      const key = `${setMeta.id}__${set.id}__${card.n}`;
      const entry = cardStates[key];
      if (entry && sharedBaseCards) {
        const item = { ...card, setTitle: set.title, setType: set.type, key };
        if (entry.status === 'wishlist') wishlistCards.push(item);
        if (entry.status === 'trade') tradeCards.push(item);
        if (entry.status === 'for_sale') forSaleCards.push({ ...item, price: entry.sale_price });
        if (entry.grading_company && entry.grading_grade) gradedCards.push({ ...item, grading: `${entry.grading_company} ${entry.grading_grade}` });
      }

      // Check parallels
      if (sharedParallelIds === null || sharedParallelIds.length > 0) {
      const allParallels = getParallelsForType(set.type);
      const parallels = sharedParallelIds ? allParallels.filter(p => sharedParallelIds.includes(p.id)) : allParallels;
      parallels.forEach(p => {
        const pKey = parallelKey(`${setMeta.id}__${set.id}`, card.n, p.id);
        const pEntry = cardStates[pKey];
        if (!pEntry) return;
        const pItem = { ...card, name: `${card.name} — ${p.name}`, setTitle: set.title, setType: set.type, key: pKey, printRun: p.print_run };
        if (pEntry.status === 'wishlist') wishlistCards.push(pItem);
        if (pEntry.status === 'trade') tradeCards.push(pItem);
        if (pEntry.status === 'for_sale') forSaleCards.push({ ...pItem, price: pEntry.sale_price });
        if (pEntry.grading_company && pEntry.grading_grade) gradedCards.push({ ...pItem, grading: `${pEntry.grading_company} ${pEntry.grading_grade}` });
      });
      } // end sharedParallels
    });
  });

  // Filter lists based on share params
  const listsToShow = sharedLists || ['wishlist', 'trade', 'for_sale', 'graded'];

  return (
    <div style={styles.page}>
      <div style={styles.starsEl} />

      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div>
            <div style={styles.logoLine}>{setMeta.brand} · {setMeta.category} · {setMeta.year}</div>
            <h1 style={styles.title}>
              <span style={styles.titleAccent}>{profile.username}</span>{isSharedView ? "'s SHARED LIST" : "'s COLLECTION"}
            </h1>
          </div>
          <a href="/" style={styles.backLink}>← BACK TO CHECKLIST</a>
        </div>
        <div style={styles.headerLine} />
      </header>

      {/* Overall stats */}
      <div style={styles.statsBar}>
        <div style={{ ...styles.pill, ...styles.pillCyan }}>TOTAL: {totalCards}</div>
        <div style={{ ...styles.pill, ...styles.pillGreen }}>✓ COLLECTED: {collectedCount}</div>
        <div style={{ ...styles.pill, ...styles.pillGold }}>COMPLETE: {pct}%</div>
        <div style={{ ...styles.pill, ...styles.pillYellow }}>★ WISHLIST: {wishlistCards.length}</div>
        <div style={{ ...styles.pill, ...styles.pillBlue }}>⇄ FOR TRADE: {tradeCards.length}</div>
        <div style={{ ...styles.pill, ...styles.pillPurple }}>$ FOR SALE: {forSaleCards.length}</div>
        {listsToShow.includes('graded') && <div style={{ ...styles.pill, borderColor: 'var(--ice)', color: 'var(--ice)' }}>🏆 GRADED: {gradedCards.length}</div>}
      </div>

      {/* Progress bar */}
      <div style={styles.progressWrap}>
        <div style={styles.progressTrack}>
          <div style={{ ...styles.progressFill, width: `${pct}%` }} />
        </div>
      </div>

      <main style={styles.main}>

        {/* Progress by set */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={{ ...styles.sectionTitle, color: 'var(--cyan)' }}>COLLECTION PROGRESS BY SET</div>
          </div>
          <div style={styles.progressGrid}>
            {SETS.filter(s => s.type !== 'sketch').map(set => {
              const total = set.cards.length;
              const done = set.cards.filter(c => cardStates[`${setMeta.id}__${set.id}__${c.n}`]?.status === 'collected').length;
              const setPct = total ? Math.round(done / total * 100) : 0;
              return (
                <div key={set.id} style={styles.setProgressItem}>
                  <div style={styles.setProgressHeader}>
                    <div style={{ ...styles.setProgressName, color: typeColor(set.type) }}>{set.title}</div>
                    <div style={styles.setProgressCount}>{done}/{total}</div>
                  </div>
                  <div style={styles.setProgressTrack}>
                    <div style={{ ...styles.setProgressFill, width: `${setPct}%`, background: typeColor(set.type) }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Wishlist */}
        {listsToShow.includes('wishlist') && wishlistCards.length > 0 && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <div style={{ ...styles.sectionTitle, color: 'var(--gold)' }}>★ WISHLIST — {wishlistCards.length} CARDS</div>
            </div>
            <div style={styles.cardGrid}>
              {wishlistCards.map(card => (
                <div key={card.key} style={{ ...styles.listCard, borderColor: 'rgba(var(--gold-rgb),0.2)', background: 'rgba(var(--gold-rgb),0.04)' }}>
                  <div style={{ ...styles.listAccent, background: 'var(--gold)' }} />
                  <CardThumbnail userId={profile.id} cardKey={card.key} isOwner={false} photosUpdatedAt={cardStates[card.key]?.photos_updated_at} />
                  <div style={styles.listInfo}>
                    <div style={styles.listNum}>{card.n}</div>
                    <div style={styles.listName}>{card.name}</div>
                    <div style={styles.listSet}>{card.setTitle}</div>
                  </div>
                  <div style={{ ...styles.listTag, color: 'var(--gold)', borderColor: 'rgba(var(--gold-rgb),0.4)' }}>★</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* For Trade */}
        {listsToShow.includes('trade') && tradeCards.length > 0 && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <div style={{ ...styles.sectionTitle, color: 'var(--cyan)' }}>⇄ FOR TRADE — {tradeCards.length} CARDS</div>
            </div>
            <div style={styles.cardGrid}>
              {tradeCards.map(card => (
                <div key={card.key} style={{ ...styles.listCard, borderColor: 'rgba(var(--cyan-rgb),0.2)', background: 'rgba(var(--cyan-rgb),0.04)' }}>
                  <div style={{ ...styles.listAccent, background: 'var(--cyan)' }} />
                  <CardThumbnail userId={profile.id} cardKey={card.key} isOwner={false} photosUpdatedAt={cardStates[card.key]?.photos_updated_at} />
                  <div style={styles.listInfo}>
                    <div style={styles.listNum}>{card.n}</div>
                    <div style={styles.listName}>{card.name}</div>
                    <div style={styles.listSet}>{card.setTitle}</div>
                  </div>
                  <div style={{ ...styles.listTag, color: 'var(--cyan)', borderColor: 'rgba(var(--cyan-rgb),0.4)' }}>⇄</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* For Sale */}
        {listsToShow.includes('for_sale') && forSaleCards.length > 0 && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <div style={{ ...styles.sectionTitle, color: 'var(--purple)' }}>$ FOR SALE — {forSaleCards.length} CARDS</div>
            </div>
            <div style={styles.cardGrid}>
              {forSaleCards.map(card => (
                <div key={card.key} style={{ ...styles.listCard, borderColor: 'rgba(var(--purple-rgb),0.2)', background: 'rgba(var(--purple-rgb),0.04)' }}>
                  <div style={{ ...styles.listAccent, background: 'var(--purple)' }} />
                  <CardThumbnail userId={profile.id} cardKey={card.key} isOwner={false} photosUpdatedAt={cardStates[card.key]?.photos_updated_at} />
                  <div style={styles.listInfo}>
                    <div style={styles.listNum}>{card.n}</div>
                    <div style={styles.listName}>{card.name}</div>
                    <div style={styles.listSet}>{card.setTitle}</div>
                  </div>
                  {card.price && (
                    <div style={{ ...styles.listTag, color: 'var(--purple)', borderColor: 'rgba(var(--purple-rgb),0.4)', fontSize: '11px' }}>
                      ${Number(card.price).toFixed(2)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Graded */}
        {listsToShow.includes('graded') && gradedCards.length > 0 && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <div style={{ ...styles.sectionTitle, color: 'var(--ice)' }}>🏆 GRADED — {gradedCards.length} CARDS</div>
            </div>
            <div style={styles.cardGrid}>
              {gradedCards.map(card => (
                <div key={card.key + '_graded'} style={{ ...styles.listCard, borderColor: 'rgba(var(--ice-rgb),0.2)', background: 'rgba(var(--ice-rgb),0.04)' }}>
                  <div style={{ ...styles.listAccent, background: 'var(--ice)' }} />
                  <CardThumbnail userId={profile.id} cardKey={card.key} isOwner={false} photosUpdatedAt={cardStates[card.key]?.photos_updated_at} />
                  <div style={styles.listInfo}>
                    <div style={styles.listNum}>{card.n}</div>
                    <div style={styles.listName}>{card.name}</div>
                    <div style={styles.listSet}>{card.setTitle}</div>
                  </div>
                  <div style={{ ...styles.listTag, color: 'var(--ice)', borderColor: 'rgba(var(--ice-rgb),0.4)', fontSize: '11px', fontWeight: 700 }}>
                    {card.grading}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {wishlistCards.length === 0 && tradeCards.length === 0 && forSaleCards.length === 0 && gradedCards.length === 0 && (
          <div style={styles.emptyState}>// NO CARDS LISTED //</div>
        )}
      </main>
    </div>
  );
}

function typeColor(type) {
  switch (type) {
    case 'base': return 'var(--cyan)';
    case 'insert': return 'var(--purple)';
    case 'auto': return 'var(--gold)';
    default: return 'var(--brand-gold)';
  }
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'Rajdhani', sans-serif", position: 'relative' },
  starsEl: {
    position: 'fixed', inset: 0, opacity: 'var(--stars-opacity)',
    backgroundImage: `
      radial-gradient(1px 1px at 10% 20%, rgba(255,255,255,0.5) 0%, transparent 100%),
      radial-gradient(1px 1px at 30% 50%, rgba(255,255,255,0.3) 0%, transparent 100%),
      radial-gradient(1.5px 1.5px at 50% 10%, rgba(255,255,255,0.6) 0%, transparent 100%),
      radial-gradient(1px 1px at 70% 70%, rgba(255,255,255,0.4) 0%, transparent 100%)
    `,
    pointerEvents: 'none', zIndex: 0,
  },
  loadScreen: { minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  loadText: { fontFamily: "'Orbitron', monospace", fontSize: '13px', letterSpacing: '4px', color: 'var(--muted-2)', textAlign: 'center' },
  notFound: { textAlign: 'center' },
  notFoundSub: { fontFamily: "'Share Tech Mono', monospace", fontSize: '12px', color: 'var(--muted-2)', marginTop: '12px' },
  homeLink: { display: 'inline-block', marginTop: '20px', fontFamily: "'Orbitron', monospace", fontSize: '10px', letterSpacing: '2px', color: 'var(--cyan)', textDecoration: 'none' },
  header: { position: 'relative', zIndex: 10, background: 'rgba(var(--bg-rgb),0.97)', padding: '24px 24px 0', borderBottom: '1px solid var(--border)' },
  headerInner: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', maxWidth: '1400px', margin: '0 auto', paddingBottom: '20px' },
  logoLine: { fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '5px', color: 'var(--brand-gold)', opacity: 0.8, marginBottom: '8px' },
  title: { fontFamily: "'Orbitron', monospace", fontSize: 'clamp(16px, 3vw, 28px)', fontWeight: 900, color: 'var(--text-strong)', margin: 0, letterSpacing: '1px', textShadow: '0 0 40px rgba(var(--cyan-rgb),0.3)' },
  titleAccent: { color: 'var(--gold)' },
  backLink: { fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '1px', color: 'var(--muted-2)', textDecoration: 'none', padding: '7px 12px', border: '1px solid var(--border)', borderRadius: '2px' },
  headerLine: { height: '1px', background: 'linear-gradient(90deg, transparent, var(--brand-gold), var(--cyan), var(--brand-gold), transparent)' },
  statsBar: { display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', padding: '12px 20px', background: 'rgba(var(--bg-rgb),0.96)', borderBottom: '1px solid var(--border)' },
  pill: { fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', padding: '5px 12px', borderRadius: '2px', border: '1px solid var(--border)', color: 'var(--muted-2)' },
  pillCyan: { borderColor: 'var(--cyan)', color: 'var(--cyan)' },
  pillGreen: { borderColor: 'var(--green)', color: 'var(--green)' },
  pillGold: { borderColor: 'var(--brand-gold)', color: 'var(--brand-gold)' },
  pillYellow: { borderColor: 'var(--gold)', color: 'var(--gold)' },
  pillBlue: { borderColor: 'var(--cyan)', color: 'var(--cyan)' },
  pillPurple: { borderColor: 'var(--purple)', color: 'var(--purple)' },
  progressWrap: { padding: '0 20px 10px', background: 'rgba(var(--bg-rgb),0.96)', borderBottom: '1px solid var(--border)' },
  progressTrack: { height: '3px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden', marginTop: '10px' },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, var(--cyan), var(--gold))', borderRadius: '2px' },
  main: { position: 'relative', zIndex: 1, padding: '20px', maxWidth: '1400px', margin: '0 auto' },
  section: { marginBottom: '32px' },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid var(--border)' },
  sectionTitle: { fontFamily: "'Orbitron', monospace", fontSize: '10px', letterSpacing: '3px', flex: 1 },
  progressGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' },
  setProgressItem: { background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '2px', padding: '10px 14px' },
  setProgressHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '6px' },
  setProgressName: { fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '1px' },
  setProgressCount: { fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: 'var(--green)' },
  setProgressTrack: { height: '3px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden' },
  setProgressFill: { height: '100%', borderRadius: '2px', transition: 'width 0.3s ease' },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '6px' },
  listCard: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', border: '1px solid', borderRadius: '2px', position: 'relative', overflow: 'hidden' },
  listAccent: { position: 'absolute', left: 0, top: 0, bottom: 0, width: '2px' },
  listInfo: { flex: 1, minWidth: 0 },
  listNum: { fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: 'var(--muted-2)', marginBottom: '2px' },
  listName: { fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: '13px', color: 'var(--text)', lineHeight: 1.2 },
  listSet: { fontFamily: "'Share Tech Mono', monospace", fontSize: '9px', color: 'var(--muted-2)', marginTop: '2px' },
  listTag: { fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '1px', padding: '2px 6px', border: '1px solid', borderRadius: '1px', flexShrink: 0 },
  emptyState: { fontFamily: "'Share Tech Mono', monospace", fontSize: '12px', color: 'var(--muted-2)', textAlign: 'center', padding: '40px', letterSpacing: '2px' },
};
